import { useCallback } from "react";
import { parsePaymentErrorFromInvokeError } from "@/lib/quotaError";
import { usePaymentErrorHandler } from "@/hooks/usePaymentErrorHandler";

/**
 * V4.3 — surfaces a 402 quota/module error from a supabase.functions.invoke()
 * call. Returns a handler that takes the invoke `error` (FunctionsHttpError),
 * and if it's a recognized 402 payment error, routes it (daily toast / monthly
 * + module PaywallSheet) and returns true. Returns false for anything else so
 * the caller falls through to its normal handling.
 *
 *   const surfaceQuota = useQuotaErrorSurface();
 *   const { data, error } = await supabase.functions.invoke(...);
 *   if (error && await surfaceQuota(error)) return null; // handled as 402
 */
export function useQuotaErrorSurface() {
  const handle = usePaymentErrorHandler();
  return useCallback(
    async (error: unknown): Promise<boolean> => {
      const pe = await parsePaymentErrorFromInvokeError(error);
      if (!pe) return false;
      handle(pe);
      return true;
    },
    [handle],
  );
}
